import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SocketService } from '@frontend/common/util';
import { merge, Observable, of, race, throwError } from 'rxjs';
import { catchError, filter, first, ignoreElements, mergeMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { Site } from '../site/site.model';

@Injectable({
  providedIn: 'root',
})
export class SiteApiService {
  constructor(private http: HttpClient, private socketSrv: SocketService) {}

  public create(entity: Site) {
    const correlation_id = entity.id;
    return merge(
      this.http.post('sites', { ...entity, correlation_id }).pipe(
        ignoreElements(),
        catchError((err) =>
          of(err?.error?.message).pipe(
            mergeMap((message) => throwError(() => ({ id: entity.id, message })))
          )
        )
      ),
      race(
        this.socketSrv
          .fromLocalSuccess<any>('sites.create')
          .pipe(filter((ws) => ws.correlation_id === correlation_id)),
        this.socketSrv.fromLocalError('sites.create').pipe(
          filter((ws) => ws.correlation_id === correlation_id),
          mergeMap(({ message }) => throwError(() => ({ id: entity.id, message })))
        )
      ).pipe(first())
    );
  }

  public delete(id) {
    const correlation_id = 'correlation|' + uuid();
    return merge(
      this.http.request('delete', `sites/${id}`, { body: { correlation_id } }).pipe(
        ignoreElements(),
        catchError((err) =>
          of(err?.error?.message).pipe(mergeMap((message) => throwError(() => ({ id, message }))))
        )
      ),
      race(
        this.socketSrv
          .fromLocalSuccess<any>('sites.remove')
          .pipe(filter((ws) => ws.correlation_id === correlation_id)),
        this.socketSrv.fromLocalError('sites.remove').pipe(
          filter((ws) => ws.correlation_id === correlation_id),
          mergeMap(({ message }) => throwError(() => ({ id, message })))
        )
      ).pipe(first())
    );
  }

  public get(params?: { [key: string]: string | string[] }): Observable<Site[]> {
    return this.http.get<Site[]>('sites', { params });
  }

  public getOne(id) {
    return this.http.get<Site>(`sites/${id}`);
  }

  public update(entity) {
    const correlation_id = 'correlation|' + uuid();
    return merge(
      this.http.put(`sites/${entity.id}`, { ...entity, correlation_id }).pipe(
        ignoreElements(),
        catchError((err) =>
          of(err?.error?.message).pipe(
            mergeMap((message) => throwError(() => ({ id: entity.id, message })))
          )
        )
      ),
      race(
        this.socketSrv
          .fromLocalSuccess<any>('sites.update')
          .pipe(filter((ws) => ws.correlation_id === correlation_id)),
        this.socketSrv.fromLocalError('sites.update').pipe(
          filter((ws) => ws.correlation_id === correlation_id),
          mergeMap(({ message }) => throwError(() => ({ id: entity.id, message })))
        )
      ).pipe(first())
    );
  }
}
